import React, { useState } from 'react';
import { RefreshCw, Package, Droplet, CheckCircle, MapPin, Clock, FileText, Printer } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

const EntregasCompletadas = ({ entregas, isLoading, onRefresh }) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [printingId, setPrintingId] = useState(null);

  const handleReimprimir = async (entrega) => {
    setIsPrinting(true);
    setPrintingId(entrega._id);
    
    try {
      const printer = getPrinterInstance();

      // Preparar datos para impresión (mismo formato que al completar la entrega)
      const datosImpresion = {
        ...entrega,
        consecutivo: entrega.consecutivo || entrega.solicitud_id,
        fechaEntrega: entrega.fecha_entrega,
        receptor_nombre: entrega.receptor_nombre,
        entregadoPor: {
          nombre: entrega.conductor?.nombre || 'Sistema'
        }
      };

      // Reimprime el mismo ticket generado al completar: dos copias
      // (Lavandería y Cliente) con espacios para firmar.
      await printer.printEntregaDobleCopia(datosImpresion);

      toast.success('Comprobante reimpreso correctamente (2 copias)');
    } catch (error) {
      console.error('Error al reimprimir:', error);
      toast.error(error.message || 'Error al reimprimir el comprobante');
    } finally {
      setIsPrinting(false);
      setPrintingId(null);
    }
  };
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-28 bg-gray-200 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (entregas.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">No hay entregas completadas hoy</p>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {entregas.map((entrega) => {
        const tipoIcon = entrega.tipo === 'lenceria' ? Package : Droplet;
        const TipoIcon = tipoIcon;

        return (
          <Card 
            key={entrega._id} 
            className="hover:shadow-md transition-shadow border-l-4 border-l-green-500 opacity-90"
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TipoIcon className={`h-5 w-5 ${
                    entrega.tipo === 'lenceria' ? 'text-blue-600' : 'text-purple-600'
                  }`} />
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {entrega.edificio?.nombre}
                    </h3>
                    {entrega.hospedaje?.nombre && (
                      <p className="text-xs text-gray-500">
                        {entrega.hospedaje.nombre}
                      </p>
                    )}
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Entregado
                </Badge>
              </div>

              {/* Información */}
              <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                <div className="bg-gray-50 rounded p-2 text-center">
                  <div className="text-gray-500">Items</div>
                  <div className="font-semibold text-gray-900">
                    {entrega.total_items || 0}
                  </div>
                </div>
                <div className="bg-gray-50 rounded p-2 text-center">
                  <div className="text-gray-500">
                    {entrega.peso_real != null ? 'Peso real' : 'Peso'}
                  </div>
                  <div className="font-semibold text-gray-900">
                    {(((entrega.peso_real != null ? entrega.peso_real : entrega.peso_total) || 0) / 1000).toFixed(1)} kg
                  </div>
                </div>
                <div className="bg-gray-50 rounded p-2 text-center">
                  <div className="text-gray-500">Tipo</div>
                  <div className="font-semibold text-gray-900 capitalize">
                    {entrega.tipo || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Información de entrega */}
              <div className="space-y-2 text-xs text-gray-600 bg-green-50 p-3 rounded">
                {entrega.fecha_entrega && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-3 w-3 text-green-600" />
                    <span>
                      Entregado: {new Date(entrega.fecha_entrega).toLocaleString('es-ES', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
                
                {entrega.conductor && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Conductor:</span>
                    <span>{entrega.conductor.nombre}</span>
                  </div>
                )}

                {entrega.receptor_nombre && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Recibido por:</span>
                    <span>{entrega.receptor_nombre}</span>
                  </div>
                )}

                {entrega.observaciones && (
                  <div className="flex items-start gap-2">
                    <span className="font-medium">Observaciones:</span>
                    <span className="flex-1">{entrega.observaciones}</span>
                  </div>
                )}
              </div>

              {/* Botón reimprimir */}
              <Button
                variant="outline"
                className="w-full mt-3"
                size="sm"
                onClick={() => handleReimprimir(entrega)}
                disabled={isPrinting && printingId === entrega._id}
              >
                <Printer className="h-4 w-4 mr-2" />
                {isPrinting && printingId === entrega._id ? 'Imprimiendo...' : 'Reimprimir Comprobante'}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default EntregasCompletadas;
